import { useEffect, useRef, useState } from 'react';
import ChevronDownIcon from '@ordr-as/icons/ChevronDownIcon';
import ListIcon from '@ordr-as/icons/ListIcon';
import type { KdsStationResponse } from '@ordr-as/ordr-service-types/epos';

export type StationSelection = { kind: 'station'; stationId: number } | { kind: 'expo' };

type Props = {
  stations: KdsStationResponse[];
  counts: Map<number, number>;
  expoCount: number;
  selection: StationSelection;
  onSelect: (selection: StationSelection) => void;
};

const CountBadge = ({ count, selected }: { count: number; selected?: boolean }) => (
  <span
    className={`text-extra-small font-semibold rounded-full px-2 py-0.5 ${
      selected ? 'bg-purple-lightest text-brand-primary' : 'bg-grey-lightest text-grey-dark'
    }`}
  >
    {count}
  </span>
);

const StationDropdown = ({ stations, counts, expoCount, selection, onSelect }: Props) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const currentStation = selection.kind === 'station' ? stations.find((s) => s.id === selection.stationId) : undefined;
  const currentLabel = selection.kind === 'expo' ? 'Expo' : currentStation?.name ?? 'Station';
  const currentCount =
    selection.kind === 'expo' ? expoCount : counts.get(selection.kind === 'station' ? selection.stationId : -1) ?? 0;

  const select = (next: StationSelection) => {
    onSelect(next);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        className="flex items-center gap-2 h-7 pl-4 pr-2 bg-brand-white border border-grey-medium rounded-full text-brand-secondary hover:border-brand-primary transition-colors"
        onClick={() => setOpen((v) => !v)}
      >
        {currentLabel}
        <CountBadge count={currentCount} />
        <ChevronDownIcon
          width={20}
          height={20}
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s ease' }}
        />
      </button>

      {open && (
        <div className="absolute left-0 z-40 mt-1 min-w-[240px] p-1 bg-brand-white border border-grey-medium rounded shadow-[0_0_30px_rgba(0,0,0,0.15)]">
          {stations.map((station) => {
            const selected = selection.kind === 'station' && selection.stationId === station.id;
            return (
              <button
                key={station.id}
                className={`flex w-full items-center justify-between gap-3 p-3 rounded text-left hover:bg-grey-lightest ${
                  selected ? 'font-semibold text-brand-secondary' : 'text-brand-black'
                }`}
                onClick={() => station.id !== undefined && select({ kind: 'station', stationId: station.id })}
              >
                {station.name ?? `Station ${station.id}`}
                <CountBadge count={counts.get(station.id ?? -1) ?? 0} selected={selected} />
              </button>
            );
          })}
          <button
            className={`flex w-full items-center justify-between gap-3 p-3 rounded text-left hover:bg-grey-lightest ${
              selection.kind === 'expo' ? 'font-semibold text-brand-secondary' : 'text-brand-black'
            }`}
            onClick={() => select({ kind: 'expo' })}
          >
            <span className="flex items-center gap-2">
              <ListIcon width={16} height={16} />
              Expo
            </span>
            <CountBadge count={expoCount} selected={selection.kind === 'expo'} />
          </button>
        </div>
      )}
    </div>
  );
};

export default StationDropdown;
